import type { Reducer } from '@reduxjs/toolkit';
import { handleActions } from 'redux-actions';

import { resourceUpserters } from '../core';
import { filtersActions } from '../filters';
import * as actions from './spendingWatchlistActions';
import {
  mkSpendingWatchList,
  mkSpendingWatchListStore,
  type SpendingWatchlistProps,
  type SpendingWatchlistStore,
  type SpendingWatchlistStoreProps,
} from './spendingWatchlistTypes';

type HandledAction = ReturnType<
  | typeof actions.createSpendingWatchList
  | typeof actions.updateSpendingWatchList
  | typeof actions.getSpendingWatchListsSuccess
  | typeof actions.getSpendingWatchListsFailure
  | typeof actions.createSpendingWatchListSuccess
  | typeof actions.updateSpendingWatchListSuccess
  | typeof actions.deleteSpendingWatchListSuccess
  | typeof actions.createSpendingWatchListFailure
  | typeof actions.updateSpendingWatchListFailure
  | typeof filtersActions.updateFilterSuccess
>;

const upsertWatchlist = (
  state: SpendingWatchlistStore,
  { payload }: { payload: SpendingWatchlistProps },
): SpendingWatchlistStore =>
  resourceUpserters.upsertResource<SpendingWatchlistProps, SpendingWatchlistStoreProps>(
    state,
    mkSpendingWatchList(payload),
  );

export const spendingWatchlistReducer: Reducer<SpendingWatchlistStore> = handleActions<
  SpendingWatchlistStore,
  HandledAction
>(
  {
    // Store optimistic creates by clientId; success replaces them with the server id.
    [actions.createSpendingWatchList.type]: upsertWatchlist,

    // Apply optimistic edits immediately, including changes to the end date.
    [actions.updateSpendingWatchList.type]: upsertWatchlist,

    [actions.getSpendingWatchListsSuccess.type]: (state, { payload: data }) =>
      resourceUpserters.upsertResources<SpendingWatchlistProps, SpendingWatchlistStoreProps>(state, data),
    [actions.getSpendingWatchListsFailure.type]: (state, { payload }) =>
      resourceUpserters.completeWithError<SpendingWatchlistProps, SpendingWatchlistStoreProps>(state, payload),

    [actions.createSpendingWatchListSuccess.type]: upsertWatchlist,
    [actions.updateSpendingWatchListSuccess.type]: upsertWatchlist,
    [actions.deleteSpendingWatchListSuccess.type]: upsertWatchlist,

    // The server rejected this create, so its optimistic row cannot acquire a server id.
    [actions.createSpendingWatchListFailure.type]: (state, { payload }) => {
      const clientId = typeof payload === 'object' && payload !== null && 'clientId' in payload && payload.clientId;
      return typeof clientId === 'string' && clientId
        ? state.set('resourcesById', state.resourcesById.delete(clientId))
        : state;
    },

    // Refetch to replace a rejected optimistic edit with the server's actual state.
    // Direct reducer calls may omit the middleware-provided asyncDispatch function.
    [actions.updateSpendingWatchListFailure.type]: (state, { asyncDispatch }) => {
      if (typeof asyncDispatch === 'function') {
        asyncDispatch(actions.getSpendingWatchLists());
      }
      return state;
    },

    [filtersActions.updateFilterSuccess.type]: (state, { asyncDispatch }) => {
      asyncDispatch?.(actions.getSpendingWatchLists());
      return state;
    },
  },
  mkSpendingWatchListStore(),
);
